import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc, Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const INTERVIEW_COST_CREDITS = 250

type InterviewStatus = Doc<'interviews'>['status']

const STATUS_STYLES: Record<InterviewStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  abandoned: 'bg-gray-100 text-gray-500',
}

const formatDurationSec = (durationSec: number): string => {
  const minPart = Math.floor(durationSec / 60)
  const secPart = durationSec % 60
  return `${minPart}m ${secPart}s`
}

type IncreaseCapacityResult =
  | { readonly ok: true; readonly newCount: number }
  | {
      readonly ok: false
      readonly reason: 'insufficient_balance'
      readonly balanceCents: number
      readonly requiredCents: number
    }
  | { readonly ok: false; readonly reason: 'not_found' }
  | { readonly ok: false; readonly reason: 'invalid_count' }

type Props = {
  readonly jobId: Id<'jobs'>
  readonly interviewCount: number
  readonly isActive: boolean
}

export const CandidatesTab = ({ jobId, interviewCount, isActive }: Props) => {
  const { t } = useTranslation()
  const [showIncrease, setShowIncrease] = useState(false)
  const [additionalSlots, setAdditionalSlots] = useState(1)
  const [increaseError, setIncreaseError] = useState<string | null>(null)

  const { data: interviews = [] } = useQuery(
    convexQuery(api.features.interviews.interviews.listMyInterviewsByJobId, {
      jobId,
    }),
  )

  const { data: candidateCount = 0 } = useQuery(
    convexQuery(api.features.candidates.candidates.countMyCandidatesByJobId, {
      jobId,
    }),
  )

  const { data: currentUser } = useQuery(
    convexQuery(api.features.user.user.getMyCurrentUser, {}),
  )

  const { mutateAsync: increaseCapacity, isPending: isIncreasing } =
    useMutation({
      mutationFn: useConvexMutation(
        api.features.jobs.jobs.increaseMyJobCapacity,
      ),
    })

  const handleIncreaseCapacity = async (): Promise<void> => {
    setIncreaseError(null)
    const result = (await increaseCapacity({
      jobId,
      additionalSlots,
    })) as IncreaseCapacityResult

    if (result.ok) {
      setShowIncrease(false)
      setAdditionalSlots(1)
      return
    }

    if (result.reason === 'insufficient_balance') {
      setIncreaseError(
        t('interviews.candidates.increaseCapacityInsufficientBalance', {
          required: String(Math.round(result.requiredCents / 100)),
          balance: String(Math.round(result.balanceCents / 100)),
        }),
      )
      return
    }

    setIncreaseError('Something went wrong.')
  }

  const balanceCents = currentUser?.wallet?.balanceCents ?? 0
  const balanceCredits = Math.round(balanceCents / 100)
  const deltaCents = additionalSlots * INTERVIEW_COST_CREDITS * 100

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-primary/60">
          {t('interviews.candidates.capacity', {
            used: String(candidateCount),
            total: String(interviewCount),
          })}
        </span>
        {isActive && candidateCount >= interviewCount && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setShowIncrease(true)
              setIncreaseError(null)
            }}
          >
            {t('interviews.candidates.increaseCapacity')}
          </Button>
        )}
      </div>

      {showIncrease && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 flex flex-col gap-3">
          <p className="text-sm font-medium text-primary">
            {t('interviews.candidates.increaseCapacityTitle')}
          </p>
          <p className="text-xs text-primary/60">
            {t('interviews.candidates.increaseCapacityDescription', {
              cost: String(INTERVIEW_COST_CREDITS),
              balance: String(balanceCredits),
            })}
          </p>
          <div className="flex items-center gap-3">
            <label className="text-xs text-primary/70 shrink-0">
              {t('interviews.candidates.additionalSlots')}
            </label>
            <input
              type="number"
              min={1}
              value={additionalSlots}
              onChange={(e) =>
                setAdditionalSlots(Math.max(1, Number(e.target.value)))
              }
              className="w-20 rounded-md border border-border bg-card px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            <span className="text-xs text-primary/50">
              = {Math.round(deltaCents / 100)} credits
            </span>
          </div>
          {increaseError && (
            <p className="text-xs text-destructive">{increaseError}</p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleIncreaseCapacity}
              disabled={isIncreasing || additionalSlots < 1}
            >
              {t('interviews.candidates.increaseCapacityConfirm')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowIncrease(false)
                setIncreaseError(null)
              }}
            >
              {t('interviews.candidates.increaseCapacityCancel')}
            </Button>
          </div>
        </div>
      )}

      {interviews.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-primary/40">
          <p className="text-sm">{t('interviews.candidates.noInterviews')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {interviews.map((interview) => (
            <div
              key={interview._id}
              className="flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-primary">
                  {interview.candidateName}
                </span>
                <span className="text-xs text-primary/50">
                  {interview.candidateEmail}
                </span>
              </div>

              <div className="flex items-center gap-3">
                {interview.durationSec !== undefined && (
                  <span className="text-xs text-primary/50">
                    {formatDurationSec(interview.durationSec)}
                  </span>
                )}
                <span
                  className={cn(
                    'rounded-full px-2.5 py-0.5 text-xs font-medium',
                    STATUS_STYLES[interview.status],
                  )}
                >
                  {interview.status === 'pending' &&
                    t('interviews.candidates.statusPending')}
                  {interview.status === 'in_progress' &&
                    t('interviews.candidates.statusInProgress')}
                  {interview.status === 'completed' &&
                    t('interviews.candidates.statusCompleted')}
                  {interview.status === 'failed' &&
                    t('interviews.candidates.statusFailed')}
                </span>
                {interview.status === 'completed' && (
                  <Link
                    to="/dashboard/interviews/$interviewId"
                    params={{ interviewId: interview._id }}
                    className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {t('interviews.candidates.viewReport')}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
